import { cookieNames, useStepper } from '@monorepo/common';
import cookies from 'js-cookie';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  makeStaticValuesForStep1,
  staticValuesForStep1,
  staticValuesForStep7
} from '../../../components/JobStepper/staticValues';
import { Step1 } from '../../../components/JobStepper/steps/Step1';
import { IStep1Form } from '../../../components/JobStepper/steps/Step1/formInterface';
import { Step7 } from '../../../components/JobStepper/steps/Step7';
import { IStep7Form } from '../../../components/JobStepper/steps/Step7/formInterfaces';
import { showNotification } from '../../../features/sliceNotification';
import { showDropButton } from '../../../features/sliceStepper';
import { ACCESS_REMOVED_MESSAGE, isForbidden } from '../../../utils/jobAccessError';
import { getResponseErrors, formatServerErrors } from '../../../utils/serverStepErrors';
import { useAppSelector } from '../../../store/hooks';
import {
  useGetReactivateJobTempDataQuery,
  useSaveReactivateStepsDataMutation,
  useReactivateJobMutation,
} from '../../../services/serviceJobData';
import { JobListingTypesResponse } from '../../../services/servicePostAJob/responseInterfaces';
// Reactivate reuses Step7 and the same PaymentIntent endpoint the Post flow uses.
import { useGetPaymentIntentMutation } from '../../../services/servicePostAJob';
import { buildReactivateIntentBody, interpretIntentResponse, GENERIC_INTENT_ERROR } from '../../../components/JobPayment';

interface IJobStepperProps {
  guid: string;
  isComplete?: boolean;
  leftInfoPanel?: ReactNode;
  finalComponent?: ReactNode;
  isStepperLoading?: boolean;
  finalStepperRequest: (val: { body: any; id: string }) => Promise<unknown>
}

export const StepperPanel = ({ guid, isComplete, isStepperLoading, finalStepperRequest, leftInfoPanel, finalComponent }:IJobStepperProps) => {
  const dispatch = useDispatch();
  const [isGetTempData, setIsGetTempData] = useState(true);
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [stepperIsDropped, setStepperIsDropped] = useState<boolean>(false);

  const { hostNumber, isStepperDrop } = useAppSelector(({ jobsContent, stepperOptions }) => ({
    hostNumber: jobsContent.hostId,
    isStepperDrop: stepperOptions.isDrop
  }));

  const [saveStepsData, { data: savedData, error: errSavedData }] = useSaveReactivateStepsDataMutation();
  const [reactivateRequest] = useReactivateJobMutation();
  const [isFirstLoadPage, setIsFirstLoadPage] = useState(true);

  // PaymentIntent ownership (see §Timing). siteId mirrors Step7's original
  // isUrlApproach logic exactly so the intent's siteId is byte-identical to before.
  const history = useHistory();
  const isUrlApproach = history.location.pathname.includes('/brewbound')
    || history.location.pathname.includes('/nosh');
  const { siteNumber } = useAppSelector(({ pathnameSettings }) => ({
    siteNumber: pathnameSettings.siteNumber
  }));
  const siteId = isUrlApproach ? siteNumber.toString() : hostNumber.toString();

  const [reachedPayment, setReachedPayment] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [getPaymentIntent] = useGetPaymentIntentMutation();

  const [isInternalLoading, setIsInternalLoading] = useState<boolean>(false);

  const { data: getSavedTempData, isLoading: getTempDataLoading, } = useGetReactivateJobTempDataQuery(cookies.get(cookieNames.tempReactivateJob) || '', {
    skip: isGetTempData
  });

  const { putSteps, onNext, onBack, onComplete, renderStepper, StepsButtons, dropStepper } = useStepper({
    orientation: 'vertical',
    leftInfoPanel: leftInfoPanel || (<></>),
    finalComponent: finalComponent || (<></>),
    isLoading: getTempDataLoading || isStepperDrop || isInternalLoading || isStepperLoading
  });

  //  steps data
  const [step1, setStep1] = useState<IStep1Form>(staticValuesForStep1);
  const [step2] = useState<IStep7Form>(staticValuesForStep7);

  const [jobListingTypes, setJobListingTypes] = useState<JobListingTypesResponse>(null);

  const createIntent = useCallback(async () => {
    setIntentError(null);
    // Invariant guard (see JobStepper): refuse to POST a PaymentIntent with an
    // out-of-range listing type. The restore now coerces listingType to 1|2, so
    // this should never fire; it surfaces an error instead of a known-bad request.
    if (step1.listingType !== 1 && step1.listingType !== 2) {
      setClientSecret(null);
      setIntentError('We could not start payment. Please reload the page and try again.');
      return;
    }
    const result = await getPaymentIntent(buildReactivateIntentBody({
      listingType: step1.listingType,
      siteId,
      standardPrice: jobListingTypes?.standardPrice,
      featuredPrice: jobListingTypes?.featuredPrice
    }));
    if ('data' in result) {
      const outcome = interpretIntentResponse(result.data);
      setClientSecret(outcome.clientSecret);
      setIntentError(outcome.error);
    } else {
      setClientSecret(null);
      setIntentError(GENERIC_INTENT_ERROR);
    }
    // primitive pricing inputs only — jobListingTypes object identity is unstable.
  }, [getPaymentIntent, step1.listingType, siteId, jobListingTypes?.standardPrice, jobListingTypes?.featuredPrice]);

  // Create the PaymentIntent when the payment step is first reached (Step1 → Step7),
  // and recreate on any pricing change afterwards. Not mount-driven: the common
  // stepper keeps every step body mounted, so that would fire at page load.
  useEffect(() => {
    if (reachedPayment) {
      createIntent();
    }
  }, [reachedPayment, createIntent]);

  const finalPaymentRequest = (val: IStep7Form) => {
    return finalStepperRequest({
      id: hostNumber,
      body: {
        guid,
        step1,
        step2: val
      }
    });
  };

  const validateFormData = async (val: IStep7Form, validateOnly: boolean): Promise<{ success: boolean, message: string }> => {
    const result = await reactivateRequest({
      id: hostNumber,
      validateOnly,
      body: {
        guid,
        step1,
        step2: val,
      }
    });
    // Any error on the validate pass must halt before payment instead of proceeding
    // on an assumed-success shape (401 is handled centrally by the base query). The
    // server's validateOnly only errors on cases that should stop the flow (missing
    // listing, already active, or access withdrawn).
    if ('error' in result) {
      if (isForbidden(result.error)) {
        return { success: false, message: ACCESS_REMOVED_MESSAGE };
      }
      // An ASP.NET ValidationProblemDetails carries a keyed `errors` map. Detect it
      // with getResponseErrors (truthy only when the map is present), then flatten it
      // to readable text with formatServerErrors — passing the WHOLE RTK error, since
      // formatServerErrors unwraps `data.errors` itself (handing it getResponseErrors'
      // output would double-unwrap → GENERIC_SERVER_ERROR).
      if (getResponseErrors(result.error)) {
        return { success: false, message: formatServerErrors(result.error) };
      }
      const data = (result.error as { data?: unknown }).data;
      const message =
        (typeof data === 'string' && data)
        || (data as { message?: string })?.message
        || (data as { error?: string })?.error
        || 'We could not validate this listing. Please try again.';
      return { success: false, message };
    }
    return { success: true, message: '' };
  };

  const steps = useMemo(() => [
    {
      label: 'Listing Type',
      content: (
        <Step1
          setIsInternalLoading={setIsInternalLoading}
          setIsRequest={setIsRequest}
          setJobListing={setStep1}
          Nav={StepsButtons}
          defaultData={step1}
          onNext={() => {
            setReachedPayment(true);
            onNext();
          }}
          setJobListingTypes={setJobListingTypes}
        />
      )
    },
    {
      label: 'Payment Information',
      content: (
        <Step7
          onBack={onBack}
          Nav={StepsButtons}
          defaultData={step2}
          onNext={onComplete}
          listingType={step1.listingType}
          finalPaymentRequest={finalPaymentRequest}
          setIsInternalLoading={setIsInternalLoading}
          validateFormData={validateFormData}
          standardPrice={jobListingTypes?.standardPrice}
          featuredPrice={jobListingTypes?.featuredPrice}
          clientSecret={clientSecret}
          intentError={intentError}
          retryIntent={createIntent}
        />
      )
    }
  ], [StepsButtons, step1, step2, clientSecret, intentError, createIntent]);

  useEffect(() => {
    dispatch(showDropButton());
    putSteps(steps);
    if (!isFirstLoadPage && isRequest) {
      saveStepsData({
        guid,
        step1,
        step2,
      });
    }
  }, [step1, step2, isRequest]);

  // Re-publish steps when the PaymentIntent state changes so Step7 receives the
  // fresh clientSecret (useStepper renders a putSteps snapshot, not live children).
  // Kept separate from the save effect above so intent changes don't re-save temp data.
  useEffect(() => {
    putSteps(steps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret, intentError, createIntent]);

  useEffect(() => {
    if (isFirstLoadPage && !cookies.get(cookieNames.tempReactivateJob)) {
      setIsFirstLoadPage(false);
    }
    if (isFirstLoadPage && cookies.get(cookieNames.tempReactivateJob)) {
      setIsGetTempData(false);
    }
  }, []);

  useEffect(() => {
    if (savedData && savedData.guid && !cookies.get(cookieNames.tempReactivateJob)) {
      cookies.set(cookieNames.tempReactivateJob, savedData.guid);
      dispatch(showDropButton());
    }
  }, [savedData, errSavedData]);

  useEffect(() => {
    if (isStepperDrop === false && cookies.get(cookieNames.tempReactivateJob)) {
      if (isFirstLoadPage && getSavedTempData) {
        // Temp-data round-trip can return step1 without a valid listingType (the
        // server read model drops it / yields 0). Never let it reach the PI request
        // as undefined — fall back to the Standard default so the Step 1 radio
        // selection and the charged tier stay consistent. (Root cause of D-33.)
        setStep1({
          ...getSavedTempData.step1,
          listingType: getSavedTempData.step1?.listingType || staticValuesForStep1.listingType,
        });
        setIsFirstLoadPage(false);
      }
    }
  }, [getSavedTempData, isStepperDrop]);

  useEffect(() => {
    if (isComplete && !stepperIsDropped) {
      setStepperIsDropped(true);
      onComplete();
      const timeout = setTimeout(() => {
        setStepperIsDropped(false);
        clearTimeout(timeout);
      }, 3000);
    }
  }, [isComplete]);

  // Reset through the factory, not the shared constant: re-assigning the same
  // reference is an Object.is no-op, so the publish effect above never re-fires
  // after `dropStepper()` emptied the steps (D-24094 §13).
  //
  // Defensive here rather than a live fix — same as JobStepper, and for the same
  // reason: this flow's first step IS JobStepper's Step1, which re-seeds step1 with
  // a fresh object on every mount, and `dropStepper()` remounts it. That masks the
  // wipe. Only PostPrepaidListing, whose first step takes no setter, actually
  // reproduces it.
  useEffect(() => {
    dispatch(showDropButton());
    if (isStepperDrop) {
      setIsRequest(false);
      setStep1(makeStaticValuesForStep1());
      dispatch(showNotification({
        show: true,
        type: 'info',
        text: 'Form cleared'
      }));
      dropStepper();
    }
  }, [isStepperDrop]);

  return renderStepper;
};
